import React, { useState, useEffect } from 'react';
import Card from './Card';
import Button from './Button';
import { getApiUrl } from '../utils/apiConfig';

interface ProjectAccessModalProps {
  projectId: string;
  isOpen: boolean;
  onAccessGranted: () => void;
  onClose?: () => void;
  title?: string;
  description?: string;
}

const ProjectAccessModal: React.FC<ProjectAccessModalProps> = ({
  projectId,
  isOpen,
  onAccessGranted,
  onClose,
  title = "Accès au projet",
  description = "Entrez le code d'accès à 6 chiffres pour voir ce projet."
}) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Réinitialiser l'état quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  // Vérifier si un mot de passe est déjà sauvegardé
  useEffect(() => {
    if (isOpen) {
      const savedPassword = localStorage.getItem(`project_${projectId}_password`);
      if (savedPassword) {
        setPassword(savedPassword);
        handleAccess(savedPassword);
      }
    }
  }, [isOpen, projectId]);

  const handleAccess = async (pass: string = password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl(`/projects/${projectId}/public?password=${pass}`));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Accès refusé');
      }

      // Sauvegarder le mot de passe
      localStorage.setItem(`project_${projectId}_password`, pass);

      // Fermer le modal et notifier l'accès accordé
      onAccessGranted();
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleAccess();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-6">{title}</h1>
        <p className="text-gray-600 text-center mb-6">
          {description}
        </p>

        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Code d'accès</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded text-center text-2xl tracking-widest"
              maxLength={6}
              placeholder="123456"
              required
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? 'Vérification...' : 'Accéder'}
            </Button>
            {onClose && (
              <Button type="button" variant="secondary" onClick={onClose}>
                Annuler
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ProjectAccessModal;
